import React, { ReactNode } from 'react';
import { plexusColors, themeColors, customTokens } from '../theme/styles/color';
import { ThemeType } from './ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
  currentTheme: ThemeType;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, currentTheme }) => {
  const currentThemeColors = themeColors[currentTheme];

  return (
    <div
      style={{
        // Theme-specific dynamic colors
        '--plexus-color-header-bg': currentThemeColors.headerBg,
        '--plexus-color-header-text': currentThemeColors.headerText,
        '--plexus-color-body-bg': currentThemeColors.bodyBg,
        '--plexus-color-body-text': currentThemeColors.bodyText,
        
        // Plexus Brand Colors
        '--plexus-color-brand-100': plexusColors.brand[100],
        '--plexus-color-brand-200': plexusColors.brand[200],
        '--plexus-color-brand-300': plexusColors.brand[300],
        '--plexus-color-brand-400': plexusColors.brand[400],
        '--plexus-color-brand-500': plexusColors.brand[500],
        '--plexus-color-brand-600': plexusColors.brand[600],
        '--plexus-color-brand-700': plexusColors.brand[700],
        '--plexus-color-brand-800': plexusColors.brand[800],
        '--plexus-color-brand-900': plexusColors.brand[900],
        
        // Plexus Positive Colors (Green)
        '--plexus-color-positive-50': '#F0FDF4', // Very light green for selected background
        '--plexus-color-positive-100': plexusColors.positive[100],
        '--plexus-color-positive-200': plexusColors.positive[200],
        '--plexus-color-positive-300': plexusColors.positive[300],
        '--plexus-color-positive-400': plexusColors.positive[400],
        '--plexus-color-positive-500': plexusColors.positive[500],
        '--plexus-color-positive-600': plexusColors.positive[600],
        '--plexus-color-positive-700': plexusColors.positive[700],
        '--plexus-color-positive-800': plexusColors.positive[800],
        '--plexus-color-positive-900': plexusColors.positive[900],
        
        // Plexus Neutral Colors
        '--plexus-color-neutral-100': plexusColors.neutral[100],
        '--plexus-color-neutral-200': plexusColors.neutral[200],
        '--plexus-color-neutral-300': plexusColors.neutral[300],
        '--plexus-color-neutral-400': plexusColors.neutral[400],
        '--plexus-color-neutral-500': plexusColors.neutral[500],
        '--plexus-color-neutral-600': plexusColors.neutral[600],
        '--plexus-color-neutral-700': plexusColors.neutral[700],
        '--plexus-color-neutral-800': plexusColors.neutral[800],
        '--plexus-color-neutral-900': plexusColors.neutral[900],
        
        // Plexus Surface Colors
        '--plexus-color-surface-50': plexusColors.surface[50],
        '--plexus-color-surface-100': plexusColors.surface[100],
        '--plexus-color-surface-200': plexusColors.surface[200],
        '--plexus-color-surface-300': plexusColors.surface[300],
        '--plexus-color-surface-400': plexusColors.surface[400],
        '--plexus-color-surface-500': plexusColors.surface[500],
        '--plexus-color-surface-600': plexusColors.surface[600],
        '--plexus-color-surface-700': plexusColors.surface[700],
        '--plexus-color-surface-800': plexusColors.surface[800],
        '--plexus-color-surface-900': plexusColors.surface[900],
        
        // Plexus Urgent Colors
        '--plexus-color-urgent-100': plexusColors.urgent[100],
        '--plexus-color-urgent-200': plexusColors.urgent[200],
        '--plexus-color-urgent-300': plexusColors.urgent[300],
        '--plexus-color-urgent-400': plexusColors.urgent[400],
        '--plexus-color-urgent-500': plexusColors.urgent[500],
        '--plexus-color-urgent-600': plexusColors.urgent[600],
        '--plexus-color-urgent-700': plexusColors.urgent[700],
        '--plexus-color-urgent-800': plexusColors.urgent[800],
        '--plexus-color-urgent-900': plexusColors.urgent[900],
        
        // Plexus Caution Colors
        '--plexus-color-caution-100': plexusColors.caution[100],
        '--plexus-color-caution-200': plexusColors.caution[200],
        '--plexus-color-caution-300': plexusColors.caution[300],
        '--plexus-color-caution-400': plexusColors.caution[400],
        '--plexus-color-caution-500': plexusColors.caution[500],
        '--plexus-color-caution-600': plexusColors.caution[600],
        '--plexus-color-caution-700': plexusColors.caution[700],
        '--plexus-color-caution-800': plexusColors.caution[800],
        '--plexus-color-caution-900': plexusColors.caution[900],
        
        // Plexus Spacing Tokens
        '--plexus-space-xs': '4px',
        '--plexus-space-sm': '8px',
        '--plexus-space-md': '16px',
        '--plexus-space-lg': '24px',
        '--plexus-space-xl': '32px',
        '--plexus-space-2xl': '48px',
        '--plexus-space-3xl': '64px',
        
        // Border tokens
        '--plexus-border-radius-sm': '4px',
        '--plexus-border-radius-md': '8px',
        '--plexus-border-radius-lg': '12px',
        '--plexus-border-radius-xl': '16px',
        '--plexus-border-width-thin': '1px',
        '--plexus-border-width-medium': '2px',
        '--plexus-border-width-thick': '4px',
        
        // Custom enhanced tokens for improved UI
        '--plexus-color-neutral-bg-subtle': customTokens.neutralBg.subtle,
        '--plexus-color-neutral-bg-hover': customTokens.neutralBg.hover,
        '--plexus-color-text-description': customTokens.text.description,
        '--plexus-color-text-muted': customTokens.text.muted,
        
        // Legacy color aliases for backward compatibility
        '--color-background': 'var(--plexus-color-body-bg)',
        '--color-surface': plexusColors.surface[50],
        '--color-text': 'var(--plexus-color-body-text)',
        '--color-text-inverse': 'var(--plexus-color-header-text)',
        '--color-neutral': plexusColors.neutral[300], // Map legacy neutral to muted text color
      } as React.CSSProperties}
    >
      {children}
    </div>
  );
};

export default ThemeProvider;
